import { styled, Typography } from '@mui/material';
import { type ReactNode, type Ref, useId } from 'react';

import type { StateColorName } from '../../../theme/tokens/stateColors';
import { Tag } from '../../atoms/Tag';

type DataAttributes = { [key: `data-${string}`]: string | number | boolean | undefined };

type Props = {
  title: string;
  /** Workflow state shown by the column, colors its count. */
  state: StateColorName;
  count: number;
  /** Accessible wording of the count, e.g. "2 incidents en cours". */
  countLabel?: string;
  /** Highlights the body while an item is dragged over it. */
  isDropTarget?: boolean;
  /** Ref of the body, the drop area of drag-and-drop libraries. */
  bodyRef?: Ref<HTMLDivElement>;
  /** Data attributes a drag-and-drop library puts on its drop area. */
  bodyProps?: DataAttributes;
  /** Cards of the column, or its empty state. */
  children: ReactNode;
  'data-testid'?: string;
};

const DROP_TARGET_TINT_OPACITY = 0.06;
/** Keeps an empty column large enough to drop into. */
const BODY_MIN_HEIGHT = 22;

const Root = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  backgroundColor: theme.palette.background.elevation1,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Count = styled('span')({
  display: 'inline-flex',
  fontVariantNumeric: 'tabular-nums',
});

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
});

const Body = styled('div')<{ ownerState: { isDropTarget: boolean } }>(({ theme, ownerState }) => ({
  flex: 1,
  minHeight: theme.spacing(BODY_MIN_HEIGHT),
  padding: theme.spacing(1.5),
  backgroundColor: ownerState.isDropTarget
    ? theme.alpha(theme.palette.primary.main, DROP_TARGET_TINT_OPACITY)
    : 'transparent',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  // Margins rather than gap: drag-and-drop libraries measure margins, not gaps.
  '& > * + *': {
    marginTop: theme.spacing(1.5),
  },
}));

/** One state column of a board (e.g. incidents *En cours* / *Terminés*). Stacks on phones, side by side from the consumer's breakpoint. */
export function KanbanColumn(props: Props) {
  const titleId = useId();

  return (
    <Root aria-labelledby={titleId} data-testid={props['data-testid']}>
      <Header>
        <Typography id={titleId} variant="subtitle1" component="h3">
          {props.title}
        </Typography>
        <Count>
          <span aria-hidden={props.countLabel ? 'true' : undefined}>
            <Tag color={props.state} label={props.count} />
          </span>
          {props.countLabel ? <VisuallyHidden>{props.countLabel}</VisuallyHidden> : null}
        </Count>
      </Header>

      <Body ref={props.bodyRef} ownerState={{ isDropTarget: props.isDropTarget ?? false }} {...props.bodyProps}>
        {props.children}
      </Body>
    </Root>
  );
}

export type { Props as KanbanColumnProps };
